import type {
  PblCase,
  PblDocumentCreatePayload,
  PblDocumentSignatureRequestPayload,
} from '@/types/pbl';
const uniqueNumbers = (values: Array<number | null | undefined>) =>
  Array.from(new Set(values.filter((value): value is number => typeof value === 'number' && Number.isFinite(value))));

const activeAssignmentStatuses = new Set(['accepted', 'invited']);
const closingAssignmentStatuses = new Set(['accepted']);

export const pblDirectCloseStatuses = [
  'meeting_scheduled',
  'in_progress',
  'pending_documents',
  'pending_completion',
  'pending_evaluation',
] as const;

export const pblEarlyCloseStatuses = ['lawyer_accepted', 'pending_lawyer_timeslot', 'pending_appointment_confirmation'] as const;
export const isPblEarlyCloseStatus = (status?: string | null) =>
  pblEarlyCloseStatuses.includes(status as typeof pblEarlyCloseStatuses[number]);

export const pblReadOnlyCaseStatuses = ['closed', 'appointment_cancelled', 'case_cancelled'] as const;

const getFiniteNumber = (value: unknown) => {
  const numberValue = Number(value);

  return Number.isFinite(numberValue) ? numberValue : undefined;
};

export const getAssignedLawyerUserIds = (caseData?: Pick<PblCase, 'assignments'> | null) =>
  uniqueNumbers(
    (caseData?.assignments || [])
      .filter((assignment) => activeAssignmentStatuses.has(assignment.status))
      .map((assignment) => assignment.lawyer_profile?.user_id)
  );

export const canClosePblCaseStatus = (status?: PblCase['status'] | string | null) =>
  pblDirectCloseStatuses.includes(status as typeof pblDirectCloseStatuses[number]);

export const isPblReadOnlyCaseStatus = (status?: PblCase['status'] | string | null) =>
  pblReadOnlyCaseStatuses.includes(status as typeof pblReadOnlyCaseStatuses[number]);

export const isAssignedPblCaseLawyer = (
  caseData?: Pick<PblCase, 'assignments'> | null,
  userData?: any,
  allowedStatuses = activeAssignmentStatuses
) => {
  const userId = getFiniteNumber(userData?.id);
  const lawyerProfileId = getFiniteNumber(userData?.pbl_lawyer_profile?.id);

  return (caseData?.assignments || []).some((assignment) => {
    if (!allowedStatuses.has(assignment.status)) return false;

    const assignmentLawyerUserId = getFiniteNumber(assignment.lawyer_profile?.user_id);
    const assignmentLawyerProfileId = getFiniteNumber(
      assignment.pbl_lawyer_profile_id ?? assignment.lawyer_profile?.id
    );

    return Boolean(
      (userId && assignmentLawyerUserId === userId) ||
        (lawyerProfileId && assignmentLawyerProfileId === lawyerProfileId)
    );
  });
};

export const canClosePblCase = (caseData?: Pick<PblCase, 'status' | 'assignments'> | null, userData?: any) => {
  if (!canClosePblCaseStatus(caseData?.status) && !isPblEarlyCloseStatus(caseData?.status)) return false;

  return isAssignedPblCaseLawyer(caseData, userData, closingAssignmentStatuses);
};

type BuildPblDocumentCreatePayloadInput = {
  caseData?: Pick<PblCase, 'assignments'> | null;
  storageFileId?: number;
  title: string;
  documentType: string;
  participantUserIds?: number[];
  includeAssignedLawyer?: boolean;
  metadata?: Record<string, unknown>;
};

export const buildPblDocumentCreatePayload = ({
  caseData,
  storageFileId,
  title,
  documentType,
  participantUserIds = [],
  includeAssignedLawyer = false,
  metadata,
}: BuildPblDocumentCreatePayloadInput): PblDocumentCreatePayload => {
  const assignedLawyerUserIds = includeAssignedLawyer ? getAssignedLawyerUserIds(caseData) : [];
  const accessUserIds = uniqueNumbers([...participantUserIds, ...assignedLawyerUserIds]);

  return {
    storage_file_id: storageFileId,
    title,
    document_type: documentType,
    ...(accessUserIds.length > 0 ? { access_user_ids: accessUserIds } : {}),
    ...(metadata ? { metadata } : {}),
  };
};

type BuildPblDocumentSignatureRequestPayloadInput = {
  caseData?: Pick<PblCase, 'assignments'> | null;
  signerUserIds?: number[];
  includeAssignedLawyer?: boolean;
  metadata?: Record<string, unknown>;
};

export const buildPblDocumentSignatureRequestPayload = ({
  caseData,
  signerUserIds = [],
  includeAssignedLawyer = false,
  metadata,
}: BuildPblDocumentSignatureRequestPayloadInput): PblDocumentSignatureRequestPayload => {
  const assignedLawyerUserIds = includeAssignedLawyer ? getAssignedLawyerUserIds(caseData) : [];

  return {
    signer_user_ids: uniqueNumbers([...signerUserIds, ...assignedLawyerUserIds]),
    ...(metadata ? { metadata } : {}),
  };
};
